/**
 * Data layer for `referral-code-tracking` (FS-826).
 *
 * Thin typed fetch wrappers over `/api/v1/referral-code-tracking`. Raw
 * `fetch()` is confined to this file per the dash contract (D6).
 *
 * Domain: per-affiliate referral codes used for partner-of-partner
 * attribution. Each code maps one affiliate to a share-URL slug. Clicks
 * through the URL land on the signup flow and any account that converts is
 * attributed back to the referring affiliate. Revenue share % is stored on
 * the code and clamped at creation time.
 *
 * Server shape (happy path, from scaffolded crud-idempotent backend):
 *   {
 *     id, tenant_id, affiliate_id, code, revenue_share_bps, status,
 *     referred_count, active_count, revenue_shared_cents,
 *     created_at, updated_at
 *   }
 *
 * The page is tolerant if a field is missing — helpers below supply
 * safe defaults when a backend row lacks a computed column.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ReferralStatus = "active" | "inactive" | "expired";

export interface ReferralCodeRow {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	code: string;
	/** Revenue share in basis points (0–3000 → 0–30 %). */
	revenue_share_bps: number;
	status: ReferralStatus;
	/** Total count of accounts that signed up through this code. */
	referred_count?: number;
	/** Subset of referred accounts that are still active on the platform. */
	active_count?: number;
	/** Lifetime revenue shared in integer cents. */
	revenue_shared_cents?: number;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListReferralCodesResponse {
	count: number;
	items: ReferralCodeRow[];
}

export interface CreateReferralCodeBody {
	affiliate_id: string;
	code?: string;
	revenue_share_bps: number;
}

export interface ReferredSignupRow {
	id: string;
	account_id: string;
	email?: string | null;
	status: "active" | "churned" | "pending";
	revenue_shared_cents?: number;
	created_at: number;
}

export interface ListSignupsResponse {
	count: number;
	items: ReferredSignupRow[];
}

export interface CheckCodeAvailabilityResponse {
	code: string;
	available: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE = "/api/v1/referral-code-tracking";

/** Max revenue share percent (server enforces, page clamps on input). */
export const MAX_REVENUE_SHARE_PERCENT = 30;

/** Min/max vanity code length (alphanumeric). */
export const VANITY_CODE_MIN = 3;
export const VANITY_CODE_MAX = 20;

const VANITY_CODE_RE = /^[A-Za-z0-9]{3,20}$/;

// ---------------------------------------------------------------------------
// Parse helpers
// ---------------------------------------------------------------------------

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// Fetchers (only raw fetch() lives in this file, D6 compliant)
// ---------------------------------------------------------------------------

export async function list(): Promise<ListReferralCodesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListReferralCodesResponse>(res);
}

export async function create(
	body: CreateReferralCodeBody,
): Promise<ReferralCodeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<ReferralCodeRow>(res);
}

export async function deactivate(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export async function listSignups(
	referralId: string,
): Promise<ListSignupsResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(referralId)}/signups`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<ListSignupsResponse>(res);
}

export async function checkAvailability(
	code: string,
): Promise<CheckCodeAvailabilityResponse> {
	const res = await fetch(
		`${BASE}/availability?code=${encodeURIComponent(code)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<CheckCodeAvailabilityResponse>(res);
}

// ---------------------------------------------------------------------------
// Query keys (TanStack Query convention — list key shared w/ invalidation)
// ---------------------------------------------------------------------------

export const referralCodeTrackingQueryKeys = {
	all: ["referral-code-tracking"] as const,
	list: () => [...referralCodeTrackingQueryKeys.all] as const,
	detail: (id: string) =>
		[...referralCodeTrackingQueryKeys.all, id] as const,
	signups: (id: string) =>
		[...referralCodeTrackingQueryKeys.all, id, "signups"] as const,
	availability: (code: string) =>
		[...referralCodeTrackingQueryKeys.all, "availability", code] as const,
};

// ---------------------------------------------------------------------------
// Validators — page calls these before mutating.
// ---------------------------------------------------------------------------

export function validateVanityCode(raw: string): string | null {
	const trimmed = raw.trim();
	if (!trimmed) return null;
	if (!VANITY_CODE_RE.test(trimmed)) return null;
	return trimmed;
}

export function isVanityCodeShape(raw: string): boolean {
	return VANITY_CODE_RE.test(raw.trim());
}

export function validateAffiliateId(raw: string): string | null {
	const trimmed = raw.trim();
	if (!trimmed) return null;
	if (trimmed.length > 128) return null;
	return trimmed;
}

/** Clamp a user-supplied percent (0–30 int) and convert to bps. */
export function clampRevenueSharePercent(n: number): number {
	if (!Number.isFinite(n)) return 0;
	const rounded = Math.round(n);
	if (rounded < 0) return 0;
	if (rounded > MAX_REVENUE_SHARE_PERCENT) return MAX_REVENUE_SHARE_PERCENT;
	return rounded;
}

export function percentToBps(pct: number): number {
	return clampRevenueSharePercent(pct) * 100;
}

export function bpsToPercent(bps: number): number {
	if (!Number.isFinite(bps) || bps < 0) return 0;
	return Math.round(bps / 100);
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatInteger(n: number | undefined | null): string {
	if (n === null || n === undefined || !Number.isFinite(n)) return "0";
	return Math.max(0, Math.floor(n)).toLocaleString("en-US");
}

export function formatPercent(bps: number | undefined | null): string {
	if (bps === null || bps === undefined || !Number.isFinite(bps)) return "0%";
	return `${bpsToPercent(bps)}%`;
}

export function formatMoneyCents(
	cents: number | undefined | null,
	currency = "USD",
): string {
	const amount = (cents ?? 0) / 100;
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount);
	} catch {
		return `$${amount.toFixed(2)}`;
	}
}

export function formatStatus(status: ReferralStatus): string {
	if (status === "active") return "Active";
	if (status === "inactive") return "Inactive";
	return "Expired";
}

/**
 * Build the public share URL from a referral code.
 * Resolves the base host in this order:
 *   1. `import.meta.env.VITE_REFERRAL_BASE_URL` (set per-env).
 *   2. `window.location.origin` (fallback for browser).
 *   3. `https://trackerity.com` (last-resort deterministic default).
 */
export function buildShareUrl(code: string): string {
	const envBase =
		typeof import.meta !== "undefined" &&
		(import.meta as { env?: Record<string, string> }).env?.VITE_REFERRAL_BASE_URL;
	const winBase =
		typeof globalThis !== "undefined" &&
		typeof (globalThis as { location?: { origin?: string } }).location
			?.origin === "string"
			? (globalThis as { location: { origin: string } }).location.origin
			: "";
	const base = envBase || winBase || "https://trackerity.com";
	return `${base.replace(/\/+$/, "")}/r/${encodeURIComponent(code)}`;
}

// ---------------------------------------------------------------------------
// KPI aggregation (pure — easy to unit-test, no React)
// ---------------------------------------------------------------------------

export interface ReferralKpis {
	total_referrals: number;
	active_sub_affiliates: number;
	revenue_shared_cents: number;
	top_referrer_id: string | null;
	top_referrer_count: number;
}

export function computeKpis(
	rows: readonly ReferralCodeRow[],
): ReferralKpis {
	let totalReferrals = 0;
	let revenueSharedCents = 0;
	const activeAffiliates = new Set<string>();
	let topId: string | null = null;
	let topCount = 0;

	for (const row of rows) {
		const referred = row.referred_count ?? 0;
		totalReferrals += referred;
		revenueSharedCents += row.revenue_shared_cents ?? 0;
		if (row.status === "active" && (row.active_count ?? 0) > 0) {
			activeAffiliates.add(row.affiliate_id);
		}
		if (referred > topCount) {
			topCount = referred;
			topId = row.affiliate_id;
		}
	}

	return {
		total_referrals: totalReferrals,
		active_sub_affiliates: activeAffiliates.size,
		revenue_shared_cents: revenueSharedCents,
		top_referrer_id: topId,
		top_referrer_count: topCount,
	};
}

// ---------------------------------------------------------------------------
// Filtering — toolbar uses this.
// ---------------------------------------------------------------------------

export function filterRows(
	rows: readonly ReferralCodeRow[],
	opts: {
		affiliate?: string;
		status?: "all" | ReferralStatus;
	},
): ReferralCodeRow[] {
	const aff = (opts.affiliate ?? "").trim().toLowerCase();
	const st = opts.status ?? "all";
	return rows.filter((row) => {
		if (aff && !row.affiliate_id.toLowerCase().includes(aff)) return false;
		if (st !== "all" && row.status !== st) return false;
		return true;
	});
}
